import React from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import {
  textStyles,
  Container,
  OutlineButton,
  outlineButtonStyles,
} from '@marketing-sites/ui'
import Link from 'next/link'
import * as TE from 'fp-ts/TaskEither'
import { pipe } from 'fp-ts/function'
import type {
  ArticlePageOtherDataQuery,
  ArticleQuery,
  LinkToArticlePageFragment,
} from '../../../.graphql/generated-types'
import type { Locale } from '@marketing-sites/utils'
import {
  notEmpty,
  isObjectWithLocaleOverrideKey,
  isLocaleType,
  siteLocale,
} from '@marketing-sites/utils'
import { ArticleBody } from '../ArticleBody'
import { ArticleHead } from '../ArticleHead'
import { RelatedArticleItem } from '../RelatedArticleItem'
import { ArticleHeroImage } from '../ArticleHeroImage'
import { ComposeComponentTopicList } from '../ComposeComponentTopicList'
import { ShareArticleButtons } from '../ShareArticleButtons'
import { StickyBanner } from '../ComposeComponentStickyBanner'
import { HorizontalImageCTA } from '../ComposeComponentHorizontalImageCTA'
import { ComposeComponentVerticalImageCTA } from '../ComposeComponentVerticalImageCTA'
import type { Substitutions } from '../../components/ContentfulRichText'
import { LinkToArticlePage } from '../LinkToArticlePage'
import type { FetchArticleError } from '../../utils/query-helpers'
import ExitPopup from '../popups/exit-popup'

import {
  logChaining,
  fetchNonLocalizedArticleQuery,
  guardAgainstArticleQueryMissingData,
  orElseReplacingOnlyMissingDataError,
  fetchAllArticles,
  fetchLocalizedArticleQuery,
  fetchNextAndPreviousArticles,
  fetchOtherDataQuery,
  fetchSubstitutions,
  getSlug,
  mkMissingDataError,
} from '../../utils/query-helpers'

export type ArticlePageData = ArticleQuery & {
  previousArticle: LinkToArticlePageFragment | null
  nextArticle: LinkToArticlePageFragment | null
}

export interface ArticlePageProps {
  kind: 'ArticlePageProps'
  data?: ArticlePageData | null
  otherData?: ArticlePageOtherDataQuery | null
  substitutions: Substitutions
}

export function ArticlePage({
  data,
  otherData,
  substitutions,
}: ArticlePageProps) {
  const [page] = data?.composePageArticleCollection?.items ?? []
  if (page) {
    const relatedArticles = page.relatedPagesCollection?.items.filter(notEmpty)
    const horizontalCTA =
      page.horizontalImageCtaOverride ??
      otherData?.composeComponentGlobalHorizontalImageCtaCollection?.items[0]
        ?.cta
    const verticalCTA =
      page.verticalImageCtaOverride ??
      otherData?.composeComponentGlobalVerticalImageCtaCollection?.items[0]?.cta
    const topicList = otherData?.composeComponentTopicListCollection?.items[0]
    const stickyBanner =
      otherData?.composeComponentStickyBannerCollection?.items[0]
    const { previousArticle, nextArticle } = data ?? {}
    return (
      <>
        <Container className="py-10">
          <>
            <Link
              href="/articles"
              className={`${textStyles.a_no_underline} mb-6 inline-block`}
            >
              ← Back to articles
            </Link>
            <ArticleHead data={page} />
            <h1 className={textStyles.h1}>{page.heading ?? page.name}</h1>
            {page.heroImage && <ArticleHeroImage data={page.heroImage} />}
            <div className="flex space-x-4">
              <div className="grow">
                <ArticleBody data={page} substitutions={substitutions} />
                <div className="my-12 flex w-full flex-col items-center space-y-8 md:flex-row md:justify-between md:space-y-0">
                  <div>
                    <Link href="/articles" passHref={true}>
                      <OutlineButton>View all articles</OutlineButton>
                    </Link>
                  </div>

                  <div className="flex flex-col space-y-8 md:flex-row md:space-x-4 md:space-y-0">
                    {previousArticle && (
                      <LinkToArticlePage
                        article={previousArticle}
                        className={outlineButtonStyles}
                      >
                        Previous article
                      </LinkToArticlePage>
                    )}
                    {nextArticle && (
                      <LinkToArticlePage
                        article={nextArticle}
                        className={outlineButtonStyles}
                      >
                        Next article
                      </LinkToArticlePage>
                    )}
                  </div>
                </div>

                <ShareArticleButtons data={page} />
              </div>
              {verticalCTA && (
                <div className="hidden flex-none md:my-6 md:block">
                  <ComposeComponentVerticalImageCTA data={verticalCTA} />
                </div>
              )}
            </div>

            {relatedArticles && relatedArticles.length > 0 && (
              <div className="mt-16 w-full space-y-4">
                <h2 className={`${textStyles.h2} text-center`}>
                  You might also like
                </h2>
                <ul className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                  {relatedArticles.map((article) => (
                    <RelatedArticleItem data={article} key={article.sys.id} />
                  ))}
                </ul>
                <div className="flex justify-center space-x-2 py-12">
                  <Link href="/articles" passHref={true}>
                    <OutlineButton>View all articles</OutlineButton>
                  </Link>
                </div>
              </div>
            )}

            {topicList && (
              <div className="mt-12">
                <h2 className={`${textStyles.h2} mb-6`}>Discover Topics</h2>
                <ComposeComponentTopicList data={topicList} />
              </div>
            )}

            {horizontalCTA && (
              <div className="mt-12">
                <HorizontalImageCTA data={horizontalCTA} />
              </div>
            )}
          </>
        </Container>

        {stickyBanner && <StickyBanner data={stickyBanner} />}
        <ExitPopup
          image="/images/Printables_1080X1080.png"
          heading="Subscribe to our newsletter"
          body="Get exclusive tips and freshly hatched content."
          successHeading="Egg-cellent! You are now subscribed."
          successBody="Cracking content will be on its way to you shortly."
          errorHeading="Oops, something went wrong."
          errorBody="Please contact support <a href='/contact-us' target='_blank'>here</a>."
          alt="education reading app"
          buttonText="Subscribe"
          tracking="/?utm_source=AUpopup&utm_medium=web&utm_campaign=lead_popup"
          eventLabel="ExitPopup-Au-HP-Test"
          cookieName="exitIntentShowLead"
          mobile={false}
          formAction="https://cloud.e.readingeggs.com/articlesleads" // Form action URL
        />
      </>
    )
  }
  return null
}

export const getStaticPathsForArticlePage: GetStaticPaths = async () => {
  const allArticles = await fetchAllArticles(siteLocale, [])
  const paths = allArticles.map(({ article }) => ({
    params: { slug: article.slug ?? '' },
  }))
  return { paths, fallback: false }
}

export const getStaticPropsForArticlePage: GetStaticProps<ArticlePageProps> = ({
  params: nullableParams,
  preview: unboundPreview,
  previewData,
}) => {
  let localeToBuildFor = siteLocale
  if (
    unboundPreview &&
    previewData &&
    isObjectWithLocaleOverrideKey(previewData) &&
    isLocaleType(previewData.localeOverride)
  ) {
    localeToBuildFor = previewData.localeOverride
  }

  const paramsTaskEither = TE.fromNullable(
    mkMissingDataError('Missing params data', []),
  )(nullableParams)
  const localeTaskEither = TE.of<FetchArticleError, Locale>(localeToBuildFor)
  const previewTaskEither = TE.of<FetchArticleError, boolean>(!!unboundPreview)

  /**
   * Bind all the TaskEithers to variables within the bound `pipe` context, sequencing the TaskEithers as we go,
   * which means if any of them "fail" (ie either the promises fail) then it will short circuit at
   * that point and "fail" the whole pipeline.
   */
  const task = pipe(
    TE.bindTo('params')(paramsTaskEither),
    TE.bind('locale', () => localeTaskEither),
    TE.bind('preview', () => previewTaskEither),
    TE.bind('slug', ({ params }) => getSlug(params)),
    TE.chainFirstW(({ preview, params, locale, slug }) =>
      logChaining({ preview, params, locale, slug }),
    ),
    TE.bind('article', ({ slug, preview, locale }) =>
      pipe(
        fetchLocalizedArticleQuery(slug, preview, locale),
        TE.chainFirstW((firstArticleFetchResult) =>
          logChaining({ firstArticleFetchResult }),
        ),
        TE.chain(guardAgainstArticleQueryMissingData),
        orElseReplacingOnlyMissingDataError(
          fetchNonLocalizedArticleQuery(slug, preview, locale),
        ),
        TE.chainFirstW((secondArticleFetchResult) =>
          logChaining({ secondArticleFetchResult }),
        ),
      ),
    ),
    TE.chainFirstW(({ article }) => logChaining({ article })),
    TE.bind('otherData', ({ preview, locale }) =>
      fetchOtherDataQuery(preview, locale),
    ),
    TE.bind('nextAndPreviousArticles', ({ slug, locale }) =>
      fetchNextAndPreviousArticles(slug, locale),
    ),
    TE.bind('substitutions', () => fetchSubstitutions),
    /**
     * Transform to just a Task by matching on the left or right cases of the inner Either, respectively:
     * - for left, we check if there are any actual errors and throw if there are, otherwise use a
     *   default blank ArticlePageProps value that will result in a blank article on the page
     *   (this happens when any of the necessary values or Contentful article data in the pipe was blank)
     * - for right, we transform the collected variables into an ArticlePageProps value
     *   (this only happens when all of the necessary dependencies could be fetched correctly)
     */
    TE.match(
      (err) => {
        // eslint-disable-next-line no-console
        console.error(err)
        const [error] = err.errors
        if (error) {
          throw error
        }
        return {
          props: {
            kind: 'ArticlePageProps' as const,
            data: null,
            otherData: null,
            substitutions: {},
          } as ArticlePageProps,
        }
      },
      ({ article, otherData, nextAndPreviousArticles, substitutions }) => ({
        props: {
          kind: 'ArticlePageProps' as const,
          data: { ...article, ...nextAndPreviousArticles },
          otherData,
          substitutions,
        },
      }),
    ),
  )

  return task()
}
